import type { Components, Theme } from '@mui/material/styles';

/**
 * The MUI v4 defaults the app keeps while v4 and v9 coexist, so a screen moved to v9 looks the way it did and
 * matches its neighbours that are still v4. Each entry is a place v9 changed a default; see "What changes
 * between v4 and v9" in docs/styling-migration.md. Once v4 is gone, delete this file and take v9's own defaults.
 */

/**
 * v4's `theme.breakpoints.down('sm')`: narrower than v4's md, 960px. v9 moved both what `down` means and md
 * itself (900px), so `useMediaQuery(theme.breakpoints.down('sm'))` no longer asks the same question.
 */
export const V4_SMALL_SCREEN = '(max-width:959.95px)';

/** v4's `<Dialog maxWidth="md">`: 960px wide at most. v9's md is 900px. Pass as the Dialog's `sx`. */
export const V4_DIALOG_MD = { '& .MuiDialog-paperWidthMd': { maxWidth: 960 } } as const;

/**
 * v4's `<Button variant="outlined">` with no colour: dark text and a grey border. v9 has no such colour and
 * falls back to primary; use with `color="inherit"`. The disabled state keeps v9's own styling, which matches.
 */
export const V4_OUTLINED_DEFAULT = { color: 'text.primary', borderColor: 'rgba(0, 0, 0, 0.23)' } as const;

/**
 * v4's `<DialogContent>` padding: 8px 24px, also right after a title. v9 pads 20px 24px and drops the top padding
 * after a title. Not a theme default, because v9-native dialogs are laid out around v9's.
 */
export const V4_DIALOG_CONTENT = { px: 3, py: 1, '.MuiDialogTitle-root + &': { pt: 1 } } as const;

/**
 * v4's table cell padding, for a `<Table sx={V4_TABLE}>`. v4 padded small cells `6px 24px 6px 16px` (16px on the last
 * cell); v9 pads them `6px 16px`, which changes auto-sized column widths. Not a theme default, because v9-native
 * tables use v9's.
 *
 * Specificity is v4's ladder: padding at two classes, so a cell class keyed after it in the same `sx` (as `'& .x'`)
 * wins, the way a makeStyles class beat v4's padding; the last-cell and checkbox rules at three and four, which
 * v4's nested rules also beat a makeStyles class with.
 */
export const V4_TABLE = {
  '& .MuiTableCell-sizeSmall': { padding: '6px 24px 6px 16px' },
  '& .MuiTableCell-sizeSmall:last-child': { paddingRight: '16px' },
  '& .MuiTableCell-sizeSmall.MuiTableCell-paddingCheckbox': { padding: '0 12px 0 16px' },
  '& .MuiTableCell-sizeSmall.MuiTableCell-paddingCheckbox:last-child': { paddingLeft: '12px', paddingRight: '16px' },
  '& .MuiTableCell-sizeSmall.MuiTableCell-paddingNone': { padding: 0 },
  '& .MuiTableCell-paddingCheckbox:last-child': { paddingLeft: 0, paddingRight: '4px' },
  // The pagination footer is a cell too; v4 kept it at 0 in any table.
  '& .MuiTablePagination-root.MuiTableCell-sizeSmall, & .MuiTablePagination-root.MuiTableCell-sizeSmall:last-child': {
    padding: 0
  }
} as const;

/** v4's IconButton padding (12px; v9 pads 8px). For an icon button that sits among v4 ones. */
export const V4_ICON_BUTTON = { p: 1.5 } as const;

/** v4's `<IconButton size="small">` padding (3px; v9 pads 5px). */
export const V4_ICON_BUTTON_SMALL = { p: '3px' } as const;

/**
 * v4's text or contained `<Button>` with no colour: dark text (and, contained, a grey fill). v9 has no such colour
 * and falls back to primary; use with `color="inherit"`.
 */
export const V4_BUTTON_DEFAULT = { color: 'text.primary' } as const;

/** v4's sticky table header, on the page background; v9 paints it white. Combine with V4_TABLE for a small table. */
export const V4_STICKY_HEADER = { '& .MuiTableCell-stickyHeader': { backgroundColor: '#fafafa' } } as const;

/** v4's filled Chip with no colour (grey[300]); v9 draws it far lighter (action.selected). */
export const V4_CHIP_DEFAULT = { bgcolor: 'grey.300' } as const;

/** v4's secondary text colour; v9 made it a shade darker (0.6). */
export const V4_TEXT_SECONDARY = 'rgba(0, 0, 0, 0.54)';

/**
 * Component defaults for the v9 theme (`muiTheme.ts`) that put back what v4 drew. Only defaults a migrated screen
 * would otherwise have to patch on every instance live here; one-off differences stay with their screen.
 *
 * Not IconButton: v9 composites (TablePagination, the date pickers, Alert) are built from it and used across the
 * app, so a default here would resize every table's pagination arrows. Pad a v4-looking one with V4_ICON_BUTTON.
 */
export const V4_COMPONENT_DEFAULTS: Components<Theme> = {
  MuiDivider: {
    styleOverrides: {
      // v4's stylesheet targets the global class .MuiDivider-root, which v9 dividers carry too. On a page where
      // a v4 Divider is mounted, its height: 1px lands on every v9 divider as well - v9 draws its line with a
      // bottom border and sets no height, so the line turns 2px. A plain horizontal v9 divider is 0px tall
      // plus its border anyway, so pinning that changes nothing where v4 is absent.
      root: ({ ownerState }) => (ownerState.orientation !== 'vertical' && !ownerState.children ? { height: 0 } : {})
    }
  },
  MuiListItemIcon: {
    // v4's icon column; v9's is 36px, which pulls every label 20px left.
    styleOverrides: { root: { minWidth: 56 } }
  },
  MuiListSubheader: {
    // v4 let a subheader take its surface's colour; v9 paints it white.
    styleOverrides: { root: { backgroundColor: 'inherit' } }
  },
  MuiTabs: {
    // v4 underlined the selected tab in the secondary colour and left the text colour alone.
    defaultProps: { indicatorColor: 'secondary', textColor: 'inherit' }
  },
  MuiTab: {
    styleOverrides: {
      // v4 meant to widen the padding to 24px from sm up, but declared that breakpoint key twice in one object, so
      // only the min-width survived. This is what it actually drew.
      root: ({ theme }) => ({
        minWidth: 72,
        maxWidth: 264,
        padding: '6px 12px',
        [theme.breakpoints.up('sm')]: { minWidth: 160 },
        '&.MuiTab-textColorInherit:not(.Mui-selected):not(.Mui-disabled)': { opacity: 0.7 }
      })
    }
  }
};
